<template>
  <div
    id="livetrack-map"
    :class="[
      'mc-delivery-track-map',
      {
        'with-history': !isLiveview,
        'with-sensor-analysis': sensorDataAnalysis,
        'map-collapsed': mapCollapsed,
      },
    ]"
  >
    <div v-if="isLoading" class="loading-overlay">
      <div v-if="!getLoaderLogoPath" class="spinner"></div>
      <img v-else :src="getLoaderLogoPath" alt="Loading..." class="loading-logo blinking" />
    </div>
    <div v-if="showBanner()" class="mc-delivery-track-started-wrapper">
      <div class="mc-delivery-track-content">
        <img width="320" class="mb-4" alt="uploading" :src="deliveryIlustration" />
        <h5 class="mc-delivery-track-title">
          {{ 'Pengemudi dalam perjalanan ke Lokasi Ambil' }}
        </h5>
        <p class="mc-delivery-track-desc">
          {{ 'Anda bisa memantau perjalanan setelah pengemudi berhasil mengambil pesanan.' }}
        </p>
      </div>
    </div>

    <div v-else :class="['mc-delivery-track-map-wrapper', { 'map-collapsed': mapCollapsed }]">
      <GmapMap
        id="map"
        ref="gmapRef"
        :center="$store.state.map.center"
        :zoom="parseFloat($store.state.map.zoom)"
        :options="gMapOptions"
        :map-type-id="$store.state.fleets.mapMode"
      >
        <!-- POINT MARKER -->
        <gmap-custom-marker
          v-for="marker in filteredMarkers"
          :key="marker.id"
          :marker="{
            lat: Number(marker.geolocation.coordinate.latitude),
            lng: Number(marker.geolocation.coordinate.longitude),
          }"
          :offsetY="0"
        >
          <div class="text-center">
            <span
              v-if="marker.type === 'ORIGIN'"
              class="mc-delivery-track-map-marker-ico-origin"
              @click="openInfo(marker)"
            >
              <i class="mc-fa icon-fill_loading"></i>
            </span>
            <span
              v-else-if="marker.type === 'TRANSIT'"
              class="mc-delivery-track-map-marker-ico-transit"
              :class="[
                'mc-delivery-track-map-marker-ico-transit',
                marker.status === 'COMPLETED' ? 'mc-delivery-track-map-marker-ico-completed' : '',
              ]"
              @click="openInfo(marker)"
            >
              <div class="circle">
                <div class="inner"></div>
              </div>
            </span>
            <span
              v-else-if="marker.type === 'DESTINATION'"
              :class="[
                'mc-delivery-track-map-marker-ico-destination',
                status !== 'Terkirim' ? 'mc-delivery-track-map-marker-ico-uncompleted' : '',
              ]"
              @click="openInfo(marker)"
            >
              <i class="mc-fa icon-fill_unloading"></i>
            </span>
            <div class="mc-liveview--map-marker-name">
              <strong>{{ marker.name }}</strong>
            </div>
          </div>
        </gmap-custom-marker>

        <!-- GEOFENCE POLYGON -->
        <GmapPolygon
          v-if="hasFeatureEditActivityTrackDOMGM"
          v-for="polygon in geofencePolygons"
          :key="polygon.key"
          :paths="polygon.paths"
          :options="geofenceOverlayOptions"
        />

        <!-- GEOFENCE CIRCLE -->
        <GmapCircle
          v-if="hasFeatureEditActivityTrackDOMGM"
          v-for="circle in geofenceCircles"
          :key="circle.key"
          :visible="true"
          :center="circle.center"
          :radius="circle.radius"
          :options="geofenceOverlayOptions"
        />

        <!-- STOPMARKER -->
        <gmap-custom-marker
          v-for="marker in stoppageMarkers"
          :key="marker.id"
          :marker="{
            lat: Number(marker.stopLat),
            lng: Number(marker.stopLong),
          }"
          :offsetY="25"
        >
          <div class="text-center">
            <img
              role="button"
              alt="Stop Icon"
              src="@/assets/images/caricon/stop-icon.svg"
              @click="openInfo(marker, 'stoppage')"
            />
          </div>
        </gmap-custom-marker>

        <GmapPolyline
          v-for="(polyline, index) in polylines"
          :key="index"
          :path="polyline.path"
          :options="polyline.options"
          @click="openInfo(polyline, 'polyline')"
        />

        <!-- VEHICLE MARKER -->
        <gmap-custom-marker
          v-if="vehicleMarker && vehicleMarker.vehicle?.vehicleType?.icon && !isLoading"
          :marker="{
            lat: Number(vehicleMarker.latitude),
            lng: Number(vehicleMarker.longitude),
          }"
          :offsetY="25"
        >
          <div class="text-center">
            <img
              :src="getVehicleIcon(vehicleMarker)"
              :style="`transform: rotate(${
                status === 'Terkirim'
                  ? parseInt(vehicleMarker?.direction) || 0
                  : parseInt(vehicle?.direction) || 0
              }deg);`"
              alt="mceasy-lifeview-car-icon"
            />
          </div>
        </gmap-custom-marker>

        <GmapInfoWindow
          v-if="activeMarker"
          :position="markers.position"
          :options="{ pixelOffset: { width: 0, height: -40 } }"
          @closeclick="closeInfo"
        >
          <div :class="['mc-delivery-track-map-info-window', { scrollable: sensorDataAnalysis }]">
            <div class="mc-delivery-track-map-info-header">
              <strong>{{ markers.title }}</strong>
              <button class="close-btn" @click="closeInfo">
                <i class="mc-fa icon-fill_close"></i>
              </button>
            </div>
            <hr />

            <!-- Info umum -->
            <div class="mc-delivery-track-map-info-body">
              <template v-for="(field, idx) in markers.fields">
                <div
                  v-if="!field.divider && field.isShow !== false"
                  :key="'field-' + idx"
                  :class="[
                    'mc-delivery-track-map-info-field',
                    { 'is-half': field.column === 'half' },
                  ]"
                >
                  <p class="mc-delivery-track-map-info-label">{{ field.label }}</p>
                  <p v-if="field.showValue !== false" class="mc-delivery-track-map-info-value">
                    <strong>{{ field.value }}</strong>
                  </p>
                </div>
                <hr
                  v-else-if="field.divider && field.isShow !== false"
                  :key="'divider-' + idx"
                  class="mc-delivery-track-map-info-divider"
                />
              </template>
            </div>
          </div>
        </GmapInfoWindow>
      </GmapMap>
      <div class="mc-delivery-track-control-panel">
        <div v-if="status !== 'Dijalankan'" class="control-left">
          <button @click="togglePopup">
            <i class="mc-fa icon-outline_info"></i>
          </button>

          <transition name="slide-up">
            <div v-if="showPopup" class="popup-panel">
              <div class="popup-header">
                <h4>{{ 'Keterangan' }}</h4>
                <span class="popup-close" @click="closePopup()">
                  <i class="mc-fa icon-fill_close"></i>
                </span>
              </div>
              <hr class="divider" />
              <div class="popup-body">
                <h5 class="text-muted">
                  {{ 'Status Kendaraan' }}
                </h5>
                <div class="status-box-vehicle mb-3">
                  <div class="status-vehicle">
                    <img :src="require(`@/assets/images/caricon/car-green.svg`)" alt="Berjalan" />
                    <p>{{ 'Berjalan' }}</p>
                  </div>
                  <div class="status-vehicle">
                    <img :src="require(`@/assets/images/caricon/car-yellow.svg`)" alt="Diam" />
                    <p>{{ 'Diam' }}</p>
                  </div>
                  <div class="status-vehicle">
                    <img :src="require(`@/assets/images/caricon/car-red.svg`)" alt="Parkir" />
                    <p>{{ 'Parkir' }}</p>
                  </div>
                  <div class="status-vehicle">
                    <img :src="require(`@/assets/images/caricon/car-blue.svg`)" alt="Assisted" />
                    <p>{{ 'Assisted' }}</p>
                  </div>
                  <div class="status-vehicle">
                    <img :src="require(`@/assets/images/caricon/car-grey.svg`)" alt="Offline" />
                    <p>{{ 'Offline' }}</p>
                  </div>
                </div>
                <h5 class="text-muted">
                  {{ 'Warna Laju Kendaraan' }}
                </h5>
                <div class="status-box-line">
                  <div class="status-line">
                    <img :src="require(`@/assets/images/speedicon/80.svg`)" alt="'> 80Km/ Jam" />
                    <p>{{ '> 80Km/ Jam' }}</p>
                  </div>
                  <div class="status-line">
                    <img
                      :src="require(`@/assets/images/speedicon/61-80.svg`)"
                      alt="61 - 80 Km/ Jam"
                    />
                    <p>{{ '61 - 80 Km/ Jam' }}</p>
                  </div>
                  <div class="status-line">
                    <img
                      :src="require(`@/assets/images/speedicon/41-60.svg`)"
                      alt="41 - 60 Km/ Jam"
                    />
                    <p>{{ '41 - 60 Km/ Jam' }}</p>
                  </div>
                  <div class="status-line">
                    <img
                      :src="require(`@/assets/images/speedicon/21-40.svg`)"
                      alt="21-40 Km/ Jam"
                    />
                    <p>{{ '21-40 Km/ Jam' }}</p>
                  </div>
                  <div class="status-line">
                    <img :src="require(`@/assets/images/speedicon/20.svg`)" alt="< 21 Km/ Jam" />
                    <p>{{ '< 21 Km/ Jam' }}</p>
                  </div>
                  <div class="status-line">
                    <img :src="require(`@/assets/images/speedicon/help.svg`)" alt="Assisted" />
                    <p>{{ 'Assisted' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <div v-if="!isLiveview" class="track-bar-detail">
          <i class="icon-fill_clock"></i>
          <p class="m-0">{{ formatDateTime() }}</p>
          <p class="m-0">|</p>

          <i class="icon-fill_driver-violation"></i>
          <p class="m-0">{{ val(liveMarker?.speed) }} Km/Jam</p>

          <!-- Temperature -->
          <p v-if="hasTmsColdChainAccess" class="m-0">|</p>
          <i v-if="hasTmsColdChainAccess" class="icon-fill_thermometer"></i>
          <p v-if="hasTmsColdChainAccess" class="m-0">{{ val(liveMarker?.temperature) }} °C</p>

          <!-- Door -->
          <p v-if="hasTmsColdChainAccess" class="m-0">|</p>
          <i
            v-if="hasTmsColdChainAccess && (liveMarker?.door1 === 0 || liveMarker?.door1 === 1)"
            :class="liveMarker.door1 === 1 ? 'icon-outline_door-open' : 'icon-outline_door-close'"
          ></i>

          <!-- LABEL -->
          <p v-if="hasTmsColdChainAccess" class="m-0">
            {{ liveMarker?.door1 === 1 ? 'Buka' : liveMarker?.door1 === 0 ? 'Tutup' : '-' }}
          </p>
        </div>

        <div v-if="status !== 'Dijalankan'" class="control-right">
          <button @click="fitToBounds()">
            <i class="mc-fa icon-outline_target"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="mc-delivery-track-control-trip">
      <div v-if="status !== 'Dijalankan' && !mapCollapsed" class="control-trip">
        <b-button-group>
          <b-button
            :variant="isLiveview ? 'info' : 'outline-info'"
            size="sm"
            @click="toggleView('live')"
          >
            <span class="live-dot" :class="{ inactive: !isLiveview }"></span>
            {{ 'Live View' }}
          </b-button>

          <b-button
            :variant="isLiveview ? 'outline-info' : 'info'"
            size="sm"
            :disabled="disabledButtonHistoryTrip"
            @click="toggleView('history')"
          >
            {{ 'Riwayat Perjalanan' }}
          </b-button>
        </b-button-group>
      </div>
    </div>

    <div
      v-if="!isLiveview"
      :class="['mc-delivery-track-history', { 'map-collapsed': mapCollapsed }]"
    >
      <McAlert
        v-if="actualPickOutOfOrder || actualDropOutOfOrder"
        class="mb-3"
        variant="warning"
        html="<strong>Rentang waktu di lokasi ambil atau kirim berbeda dengan perjalanan kendaraan"
        withIcon
        customIcon="icon-fill_info"
        :withGap="false"
        iconPosition="center"
      />
      <div class="track-bar-container">
        <!-- Bar progres -->
        <div
          ref="trackBar"
          class="track-bar"
          @mousedown="startDrag"
          @touchstart.prevent="startDrag"
        >
          <div class="track-progress" :style="{ width: progressPercent + '%' }"></div>
          <div
            class="track-circle"
            :style="{ left: progressPercent + '%' }"
            @mousedown.stop="startDrag"
            @touchstart.stop.prevent="startDrag"
          ></div>
        </div>

        <!-- Kontrol playback -->
        <div class="track-controls">
          <div class="controls-left">
            <button class="play-btn" title="Play/Pause" @click="togglePlay">
              <i :class="isPlaying ? 'icon-fill_pause' : 'icon-fill_play'"></i>
            </button>

            <button class="prev-btn" title="Mundur 10 detik" @click="rewind10">
              <i class="icon-outline_arrowhead-left"></i>
            </button>

            <button class="next-btn" title="Maju 10 detik" @click="forward10">
              <i class="icon-outline_arrowhead-right"></i>
            </button>

            <select v-model="playSpeed">
              <option v-for="s in speeds" :key="s" :value="s">{{ s }}{{ 'x' }}</option>
            </select>

            <span class="time-display">
              {{ formatTime(currentTime) }} / {{ formatTime(totalTime) }}
            </span>
          </div>

          <div class="controls-right">
            <div v-if="hasFeatureEditActivityTrackDOMGM" class="date-range-history">
              <FormulateInput
                :key="historyDateRangeKey"
                v-model="historyDateRange"
                name="dateRange"
                type="date-picker"
                datepickerType="datetime"
                value-type="YYYY-MM-DDTHH:mm:ss.SSSZ"
                format="DD MMM YYYY HH:mm"
                :range="true"
                :draft="true"
                :confirm="true"
                :cancel="true"
                confirmText="Terapkan"
                :clearable="false"
                :disabled="isEditingLocationTime"
                :disabled-date="disabledHistoryDate"
                :available-range-text="historyAvailableRangeText"
                validation="customDateValidation"
                validation-behavior="live"
                error-behavior="live"
                :validation-rules="{ customDateValidation }"
                :validation-messages="{
                  customDateValidation: 'Tanggal harus dalam rentang yang tersedia dan maksimal 72 jam',
                }"
              />
              <span
                v-if="historyDateRangeChanged"
                class="date-range-history__indicator"
                aria-hidden="true"
              ></span>
            </div>

            <select v-if="isMultiDoor" v-model="selectedDoor">
              <option>{{ 'Pintu 1' }}</option>
              <option>{{ 'Pintu 2' }}</option>
            </select>

            <select v-if="isMultiChamber" v-model="selectedTemperature">
              <option>{{ 'Suhu 1' }}</option>
              <option>{{ 'Suhu 2' }}</option>
              <option>{{ 'Suhu 3' }}</option>
            </select>

            <div class="sensor-box">
              <div class="form-check form-switch form-switch-sm">
                <input
                  id="switchChart"
                  v-model="sensorDataAnalysis"
                  class="form-check-input"
                  type="checkbox"
                />
                <label for="switchChart" class="switch-label">
                  {{ 'Analisa Sensor' }}
                  <i class="icon-outline_trending-up"></i>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="sensorDataAnalysis"
        :class="['analysis-container', { 'map-collapsed': mapCollapsed }]"
      >
        <hr class="mt-0" />
        <div class="analysis-info">
          <div class="analysis-info-left">
            <p class="me-2">
              <strong>{{ isChart ? 'Grafik Sensor' : 'Tabel Sensor' }}</strong>
            </p>
            <McBadge
              v-if="isMultiChamber"
              :text="selectedTemperature"
              variant="outlineWarning"
              size="medium"
              type="button"
              :withDot="true"
              class="me-2"
            />
            <McBadge
              v-if="isMultiDoor"
              :text="selectedDoor"
              variant="outlineInfo"
              size="medium"
              type="button"
              :withDot="true"
            />
          </div>

          <div class="analysis-info-right">
            <b-button
              v-if="hasTmsColdChainAccess"
              variant="outline-info me-2"
              :disabled="isChart"
              @click="handleDownload"
            >
              <i class="icon-fill_download"></i>
            </b-button>

            <b-button variant="outline-info me-2" @click="handleExpand">
              <i :class="mapCollapsed ? 'icon-outline_collapse' : 'icon-outline_expand'"></i>
            </b-button>

            <b-button-group v-if="hasTmsColdChainAccess">
              <b-button
                :variant="isChart ? 'info' : 'outline-info'"
                @click="toggleAnalysis('chart')"
              >
                <span><i class="icon-outline_trending-up"></i></span>
                {{ 'Grafik' }}
              </b-button>

              <b-button
                v-if="hasTmsColdChainAccess"
                :variant="isChart ? 'outline-info' : 'info'"
                @click="toggleAnalysis('table')"
              >
                {{ 'Tabel' }}
              </b-button>
            </b-button-group>
          </div>
        </div>

        <div class="analysis-content">
          <div v-if="isChart" class="analysis-chart-wrapper">
            <LineChart
              :key="chartRenderKey"
              ref="lineChart"
              :labels="chartData.labels"
              :datasets="chartData.datasets"
              :options="chartOptions"
            />
          </div>

          <McTable
            v-else
            ref="mcTable"
            :key="deliveryTableKey"
            :class="{ 'analysis-table-fullscreen': mapCollapsed }"
            :fields="computedFields"
            :apiClient="apiClient"
            :stickyHeader="true"
            :withCard="false"
            :hasCheckbox="false"
            :hasSelectAll="false"
          >
            <template #cell(sent_on)="{ value }">
              {{ value }}
            </template>
            <template #cell(longitude)="{ value }">
              {{ value }}
            </template>
            <template #cell(latitude)="{ value }">
              {{ value }}
            </template>
          </McTable>
        </div>
      </div>
      <div
        v-if="showVerticalLine && isChart"
        class="vertical-line"
        :style="{
          left: verticalX + 'px',
          top: verticalLineTop + 'px',
          height: verticalLineHeight + 'px',
          borderColor: verticalLineColor,
        }"
      ></div>

      <div
        v-if="tooltipVisible && isChart"
        class="custom-tooltip"
        :style="{ left: tooltipPos.x + 'px', top: tooltipPos.y + 'px' }"
      >
        <div class="tooltip-header">
          {{ tooltipTitle }}
        </div>

        <div v-for="(item, i) in tooltipItems" :key="i" class="tooltip-item">
          <span class="dot" :style="{ background: item.color }"></span>
          <span class="label">{{ item.label }}:</span>
          <span class="value">{{ item.value }}</span>
        </div>
      </div>
    </div>

    <div class="mc-delivery-track-footer">
      <span>
        {{ 'Terakhir diupdate pada :' }}
        {{ formatIndexDate(currentDateTime).date + ', ' + formatIndexDate(currentDateTime).time }}
      </span>
      <div>
        <span
          :hidden="
            getAsset('copyrightLogo', 'general') == null ||
            getAsset('copyrightLogo', 'general') == ''
          "
          >{{ $t('global.text.poweredBy') }} :
        </span>
        <figure class="d-inline-block mb-0">
          <img ref="imgCopyrightLogo" :src="getAsset('copyrightLogo', 'general')" alt />
        </figure>
      </div>
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import responsive from '@/mixin/responsive'

import LineChart from '@/components/Chart/Line'
import McBadge from '@/components/McBadge'
import McTable from '@/components/McTable'
import McAlert from '@/components/McAlert'

import { mapGetters } from 'vuex'
import { gmapApi } from 'vue2-google-maps'
import GmapCustomMarker from 'vue2-gmap-custom-marker'
import { formatIndexDate, getDuration } from '@/helpers/tzDate'
import deliveryIlustration from '@/assets/images/delivery.png'

import { GetAddressData } from '@/services/api/map'
import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'
import { EventBus } from '@/router/views/delivery/track/lib/event-bus.js'

import moment from 'moment'
moment.locale('id')

export default {
  name: 'GmapFleet',
  components: {
    'gmap-custom-marker': GmapCustomMarker,
    LineChart,
    McBadge,
    McTable,
    McAlert,
  },
  mixins: [Helper, responsive],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: null,
    },
    delivery: {
      type: Object,
      default: null,
    },
    vehicle: {
      type: Object,
      default: null,
    },
    detailTripHistories: {
      type: Object,
      default: null,
    },
    currentDateTime: {
      type: String,
      default: null,
    },
    hasTmsColdChainAccess: {
      type: Boolean,
      default: false,
    },
    isMultiDoor: {
      type: Boolean,
      default: false,
    },
    isMultiChamber: {
      type: Boolean,
      default: false,
    },
    uuid: {
      type: String,
      default: null,
    },
    hasFeatureEditActivityTrackDOMGM: {
      type: Boolean,
      default: false,
    },
    isEditingLocationTime: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:currentDateTime', 'marker-selected', 'isLiveview', 'history-date-range-change'],
  data() {
    return {
      gMapOptions: {
        minZoom: 3,
        disableDefaultUI: true,
        styles: [
          {
            featureType: 'administrative',
            elementType: 'geometry',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          {
            featureType: 'road',
            elementType: 'labels.icon',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'transit',
            elementType: 'labels.icon',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'geometry.fill',
            stylers: [
              {
                color: '#d5e6f9',
              },
            ],
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'geometry.stroke',
            stylers: [
              {
                color: '#d5e6f9',
              },
            ],
          },
          {
            featureType: 'landscape',
            elementType: 'geometry.fill',
            stylers: [
              {
                weight: '1',
              },
              {
                color: '#d5e6f9',
              },
              {
                saturation: '0',
              },
            ],
          },
        ],
      },
      deliveryIlustration,
      center: { lat: -6.9175, lng: 107.6191 },
      activeMarker: false,
      markers: {},
      polylines: [],
      showPopup: false,
      isLiveview: true,

      isPlaying: false,
      currentTime: 0,
      totalTime: 0,
      playSpeed: 1,
      speeds: [4, 2, 1.5, 1, 0.5, 0.25],
      timer: null,
      selectedDoor: 'Pintu 1',
      selectedTemperature: 'Suhu 1',
      sensorDataAnalysis: false,
      isChart: true,
      liveMarker: null,
      deliveryTableKey: 0,
      historyDateRange: null,
      initialHistoryDateRange: null,
      historyDateRangeKey: 0,
      visibleChartDatasets: [],

      tooltipVisible: false,
      tooltipTitle: '-',
      tooltipItems: [],
      tooltipPos: { x: 90, y: 400 },

      showVerticalLine: false,
      verticalX: 0,
      verticalLineTop: 0,
      verticalLineHeight: 0,
      verticalLineColor: '#99a1b1',

      infoSensorSnapshotCache: {},
      infoSensorSnapshotRequests: {},
      mapCollapsed: false,
      chartRenderKey: 0,
    }
  },
  computed: {
    google: gmapApi,
    ...mapGetters({
      getAsset: 'asset/getAsset',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId())
      )
    },
    isLoading() {
      return this.loading
    },
    pickupCompletedOn() {
      if (this.delivery?.shipment_type === 'TRANSIT') {
        const range = this.getTransitDateRange()
        return range?.start || null
      }
      const pickup = this.delivery?.delivery_order_activity?.pickup
      return pickup?.geofence_in || pickup?.completed_on || null
    },
    dropCompletedOn() {
      if (this.delivery?.shipment_type === 'TRANSIT') {
        const range = this.getTransitDateRange()
        return range?.end || null
      }
      const drop = this.delivery?.delivery_order_activity?.drop
      return drop?.geofence_out || drop?.completed_on || null
    },
    historyRangeEndDate() {
      if (this.delivery?.shipment_type === 'TRANSIT') {
        return this.dropCompletedOn || this.currentDateTime || new Date()
      }

      if (this.status === 'Terkirim') {
        return this.dropCompletedOn || this.currentDateTime || new Date()
      }

      return this.currentDateTime || new Date()
    },
    historyAvailableRangeText() {
      if (
        !['Diambil', 'Terkirim', 'Sedang Transit'].includes(this.status) ||
        !this.pickupCompletedOn
      )
        return ''

      const start = this.formatAvailableDateTime(this.pickupCompletedOn)
      let endReference = new Date(this.historyRangeEndDate)

      if (this.delivery?.shipment_type !== 'TRANSIT') {
        // Rentang filter tersedia adalah durasi asli + 3 hari
        endReference.setDate(endReference.getDate() + 3)
      }

      // Cap endReference ke currentDateTime jika melebihi
      const now = new Date(this.currentDateTime || new Date())
      if (endReference > now) {
        endReference = new Date(now)
      }

      const end = this.formatAvailableDateTime(endReference)

      if (!start || !end) return ''
      return `${start} - ${end}`
    },
    historyDateRangeChanged() {
      if (!Array.isArray(this.historyDateRange) || this.historyDateRange.length !== 2) return false
      if (
        !Array.isArray(this.initialHistoryDateRange) ||
        this.initialHistoryDateRange.length !== 2
      ) {
        return false
      }

      return this.historyDateRange.some(
        (value, index) => value !== this.initialHistoryDateRange[index]
      )
    },
    pickupActivityRaw() {
      const originId = this.delivery?.origin_address?.id
      const isTransit = this.delivery?.shipment_type === 'TRANSIT'

      return isTransit
        ? this.delivery?.transits?.find((t) => t.origin_address?.id === originId)
            ?.delivery_order_activity?.pickup
        : this.delivery?.delivery_order_activity?.pickup || {}
    },
    actualPickOutOfOrder() {
      return this.pickupActivityRaw?.is_activity_out_of_order
    },
    dropActivityRaw() {
      const destinationId = this.delivery?.destination_address?.id
      const isTransit = this.delivery?.shipment_type === 'TRANSIT'

      return isTransit
        ? this.delivery?.transits?.find((t) => t.destination_address?.id === destinationId)
            ?.delivery_order_activity?.drop
        : this.delivery?.delivery_order_activity?.drop || {}
    },
    actualDropOutOfOrder() {
      return this.dropActivityRaw?.is_activity_out_of_order
    },
    getLoaderLogoPath() {
      return this.getAsset('secondaryLogo', 'sidebarMinimize')
    },
    filteredMarkers() {
      const result = []

      if (this.delivery?.origin_address) {
        const geo = this.delivery.origin_address.geolocation
        if (this.isValidCoordinate(geo)) {
          result.push({
            ...this.delivery.origin_address,
            type: 'ORIGIN',
          })
        }
      }

      if (Array.isArray(this.delivery?.transits)) {
        this.delivery.transits.forEach((t, idx) => {
          const addr = t.destination_address
          if (!addr) return

          const geo = addr.geolocation
          if (!this.isValidCoordinate(geo)) return

          const isLastTransit = idx === this.delivery.transits.length - 1
          const isSameAsFinalDestination =
            this.delivery.destination_address && addr.id === this.delivery.destination_address.id

          if (isLastTransit && isSameAsFinalDestination) return

          result.push({
            ...addr,
            type: 'TRANSIT',
            sequence: t.sequence,
            transit_id: t.id,
            status: t.status,
            delivery_order_activity: t.delivery_order_activity,
          })
        })
      }

      if (this.delivery?.destination_address) {
        const geo = this.delivery.destination_address.geolocation
        if (this.isValidCoordinate(geo)) {
          result.push({
            ...this.delivery.destination_address,
            type: 'DESTINATION',
          })
        }
      }

      return result
    },
    stoppageMarkers() {
      if (this.status !== 'Terkirim') {
        return this.detailTripHistories?.stoppage?.slice(0, -1)
      } else if (!this.customDateValidation({ value: this.historyDateRange })) {
        return []
      }
      return this.detailTripHistories?.stoppage
    },
    geofenceOverlayOptions() {
      return {
        strokeColor: '#033380',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#4c8df5',
        fillOpacity: 0.35,
        clickable: false,
      }
    },
    geofencePolygons() {
      return this.filteredMarkers
        .map((marker) => {
          const geolocation = marker?.geolocation
          const type = String(geolocation?.type || '').toUpperCase()
          if (type !== 'POLYGON' || !Array.isArray(geolocation?.polygon)) return null

          const paths = geolocation.polygon
            .map((item) => ({
              lat: Number(item?.latitude),
              lng: Number(item?.longitude),
            }))
            .filter((item) => this.isValidLatLng(item.lat, item.lng))

          if (paths.length < 3) return null

          return {
            key: `geofence-polygon-${marker.type}-${marker.id}`,
            paths,
          }
        })
        .filter(Boolean)
    },
    geofenceCircles() {
      return this.filteredMarkers
        .map((marker) => {
          const geolocation = marker?.geolocation
          const type = String(geolocation?.type || '').toUpperCase()
          if (type !== 'CIRCLE') return null

          const lat = Number(geolocation?.coordinate?.latitude)
          const lng = Number(geolocation?.coordinate?.longitude)
          if (!this.isValidLatLng(lat, lng)) return null

          const radius = Number(geolocation?.radius) || 100

          return {
            key: `geofence-circle-${marker.type}-${marker.id}`,
            center: { lat, lng },
            radius,
          }
        })
        .filter(Boolean)
    },
    vehicleMarker() {
      const lastPosition = this.detailTripHistories?.data
        ?.slice()
        .reverse()
        .find((item) => !item.break)

      if (!this.vehicle || !lastPosition) return null

      return {
        ...this.vehicle,
        ...lastPosition,
        ...(this.liveMarker || {}),
      }
    },
    progressPercent() {
      return (this.currentTime / this.totalTime) * 100
    },
    computedFields() {
      const fields = [
        {
          key: 'sent_on',
          label: 'Waktu',
          isSortable: false,
          isFirstRowClickable: false,
          isRedirectable: false,
          isShow: true,
          formatter: (value) => {
            return formatIndexDate(value).date + ',' + formatIndexDate(value).time
          },
        },
        {
          key: 'longitude',
          label: 'Longitude',
          isSortable: false,
          isShow: true,
        },
        {
          key: 'latitude',
          label: 'Latitude',
          isSortable: false,
          isShow: true,
        },
        {
          key: 'geofence_name',
          label: 'Nama Lokasi',
          isSortable: false,
          isShow: this.isMGM,
          formatter: (value) => {
            return value || '-'
          },
        },
        {
          key: 'address',
          label: 'Detail Alamat',
          isSortable: false,
          isShow: true,
          isEllipsis: true,
          formatter: (value) => {
            return value || '-'
          },
        },
        {
          key: 'speed',
          label: 'Kecepatan',
          isSortable: false,
          isShow: true,
          formatter: (value) => {
            return value + ' Km/Jam'
          },
        },
        {
          key: 'acc',
          label: 'ACC',
          isSortable: false,
          isShow: true,
          formatter: (value) => {
            if (value === 1) return 'Nyala'
            if (value === 0) return 'Mati'
            return '-'
          },
        },
        {
          key: 'door',
          label: this.isMultiDoor
            ? this.selectedDoor === 'Pintu 1'
              ? 'Pintu 1'
              : this.selectedDoor === 'Pintu 2'
              ? 'Pintu 2'
              : 'Pintu'
            : 'Pintu',
          isSortable: false,
          isShow: this.hasTmsColdChainAccess,
          formatter: (value) => {
            if (value === 1) return 'Buka'
            if (value === 0) return 'Tutup'
            return '-'
          },
        },
        {
          key: 'refrigerator',
          label: 'Refrigerator',
          isSortable: false,
          isShow: this.hasTmsColdChainAccess,
          formatter: (value) => {
            if (value === 1) return 'ON'
            if (value === 0) return 'OFF'
            if (value === null || value === undefined) return '-'
          },
        },
        {
          key: 'temperature',
          label: this.isMultiChamber
            ? this.selectedTemperature === 'Suhu 1'
              ? 'Suhu 1'
              : this.selectedTemperature === 'Suhu 2'
              ? 'Suhu 2'
              : this.selectedTemperature === 'Suhu 3'
              ? 'Suhu 3'
              : 'Suhu'
            : 'Suhu',
          isSortable: false,
          isShow: this.hasTmsColdChainAccess,
          formatter: (value) => {
            if (value === null || value === undefined) return '-'
            return value + ' °C'
          },
        },
      ]

      return fields.filter((f) => f.isShow)
    },
    chartData() {
      const hasHistoryDateRange =
        Array.isArray(this.historyDateRange) && this.historyDateRange.length === 2
      const hasInvalidHistoryDateRange =
        hasHistoryDateRange && !this.customDateValidation({ value: this.historyDateRange })
      if (hasInvalidHistoryDateRange) return { labels: [], datasets: [] }

      const data = this.detailTripHistories?.data?.filter((item) => !item.break) || []
      if (!data.length) return { labels: [], datasets: [] }
      const scaleMetrics = this.getChartScaleMetrics()

      const datasets = [
        {
          label: 'Kecepatan',
          data: data.map((i) => i.speed || 0),
          borderColor: '#8F49DE',
          backgroundColor: '#8F49DE',
          fill: false,
          tension: 0.2,
          pointRadius: 2,
          pointHoverRadius: 4,
          hidden: !this.isLegendDatasetVisible('Kecepatan'),
        },
      ]

      if (this.hasTmsColdChainAccess) {
        let temperatureData = []
        switch (this.selectedTemperature) {
          case 'Suhu 2':
            temperatureData = data.map((i) =>
              i.temperature2 === null || i.temperature2 === undefined
                ? null
                : scaleMetrics.tempStart +
                  (this.clampValue(
                    Number(i.temperature2),
                    scaleMetrics.tempAxisMin,
                    scaleMetrics.tempAxisMax
                  ) -
                    scaleMetrics.tempAxisMin) *
                    scaleMetrics.tempScale
            )
            break

          case 'Suhu 3':
            temperatureData = data.map((i) =>
              i.temperature3 === null || i.temperature3 === undefined
                ? null
                : scaleMetrics.tempStart +
                  (this.clampValue(
                    Number(i.temperature3),
                    scaleMetrics.tempAxisMin,
                    scaleMetrics.tempAxisMax
                  ) -
                    scaleMetrics.tempAxisMin) *
                    scaleMetrics.tempScale
            )
            break

          default:
            temperatureData = data.map((i) =>
              i.temperature === null || i.temperature === undefined
                ? null
                : scaleMetrics.tempStart +
                  (this.clampValue(
                    Number(i.temperature),
                    scaleMetrics.tempAxisMin,
                    scaleMetrics.tempAxisMax
                  ) -
                    scaleMetrics.tempAxisMin) *
                    scaleMetrics.tempScale
            )
            break
        }

        datasets.push({
          label: 'Suhu',
          data: temperatureData,
          borderColor: '#FF8A00',
          backgroundColor: '#FF8A00',
          fill: false,
          tension: 0.2,
          pointRadius: 2,
          pointHoverRadius: 4,
          customTooltip: true,
          hidden: !this.isLegendDatasetVisible('Suhu'),
        })

        const tempUpperLimit = this.delivery?.load_max_temperature
        const tempLowerLimit = this.delivery?.load_min_temperature

        if (tempUpperLimit !== null && tempUpperLimit !== undefined) {
          datasets.push({
            label: 'Batas Atas Suhu',
            data: Array(data.length).fill(
              scaleMetrics.tempStart +
                (this.clampValue(
                  Number(tempUpperLimit),
                  scaleMetrics.tempAxisMin,
                  scaleMetrics.tempAxisMax
                ) -
                  scaleMetrics.tempAxisMin) *
                  scaleMetrics.tempScale
            ),
            borderColor: '#f41d00',
            borderDash: [5, 5],
            fill: false,
            pointRadius: 0,
            borderWidth: 1,
            customTooltip: false,
            hidden: !this.isLegendDatasetVisible('Suhu'),
          })
        }

        if (tempLowerLimit !== null && tempLowerLimit !== undefined) {
          datasets.push({
            label: 'Batas Bawah Suhu',
            data: Array(data.length).fill(
              scaleMetrics.tempStart +
                (this.clampValue(
                  Number(tempLowerLimit),
                  scaleMetrics.tempAxisMin,
                  scaleMetrics.tempAxisMax
                ) -
                  scaleMetrics.tempAxisMin) *
                  scaleMetrics.tempScale
            ),
            borderColor: '#137c55',
            borderDash: [5, 5],
            fill: false,
            pointRadius: 0,
            borderWidth: 1,
            customTooltip: false,
            hidden: !this.isLegendDatasetVisible('Suhu'),
          })
        }

        let doorData = []

        switch (this.selectedDoor) {
          case 'Pintu 2':
            doorData = data.map((i) => {
              const v = i.door2
              if (v === 1) return scaleMetrics.doorOpen
              if (v === 0) return scaleMetrics.doorClosed
              return null
            })
            break

          default:
            doorData = data.map((i) => {
              const v = i.door1
              if (v === 1) return scaleMetrics.doorOpen
              if (v === 0) return scaleMetrics.doorClosed
              return null
            })
            break
        }

        datasets.push({
          label: 'Pintu',
          data: doorData,
          borderColor: '#4990E1',
          backgroundColor: '#4990E1',
          fill: false,
          steppedLine: true,
          tension: 0,
          pointRadius: 2,
          pointHoverRadius: 4,
          customTooltip: true,
          hidden: !this.isLegendDatasetVisible('Pintu'),
        })
      }

      const uniqueDates = [...new Set(data.map((i) => formatIndexDate(i.sentOn).date))]
      const sameDate = uniqueDates.length === 1

      const total = data.length
      const p25 = Math.round(total * 0.25)
      const p50 = Math.round(total * 0.5)
      const p75 = Math.round(total * 0.75)

      const labels = data.map((i, idx) => {
        const f = formatIndexDate(i.sentOn)
        const labelText = sameDate ? f.time : `${f.date} ${f.time}`

        if (idx === 0) return labelText
        if (idx === p25) return labelText
        if (idx === p50) return labelText
        if (idx === p75) return labelText
        if (idx === total - 1) return labelText

        return ''
      })

      return {
        labels,
        datasets,
      }
    },
    chartOptions() {
      const axisConfig = this.getChartAxisConfig()
      const vm = this

      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          onClick: function (e, legendItem) {
            const labelsToToggle =
              legendItem.text === 'Suhu'
                ? ['Suhu', 'Batas Atas Suhu', 'Batas Bawah Suhu']
                : [legendItem.text]

            vm.toggleVisibleChartDatasets(labelsToToggle)
            vm.tooltipVisible = false
            vm.showVerticalLine = false
            vm.$nextTick(() => {
              if (vm.$refs.lineChart && vm.$refs.lineChart.renderChart) {
                vm.$refs.lineChart.renderChart(vm.chartData, vm.chartOptions)
              }
            })
          },
          labels: {
            boxWidth: 25,
            padding: 15,
            filter: function (legendItem, chartData) {
              const hiddenLabels = ['Batas Atas Suhu', 'Batas Bawah Suhu']
              return !hiddenLabels.includes(legendItem.text)
            },
          },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        tooltips: {
          enabled: false,
          mode: 'index',
          intersect: true,
          axis: 'x',
          custom: (tooltipModel) => this.handleHoverTooltip(tooltipModel),
        },
        scales: {
          xAxes: [
            {
              ticks: {
                autoSkip: false,
                maxRotation: 0,
                callback: function (value, index, values) {
                  return value === '' ? null : value
                },
              },
              gridLines: {
                color: '#eee',
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                min: 0,
                max: axisConfig.max,
                stepSize: axisConfig.stepSize,
                callback: axisConfig.tickCallback,
              },
              afterBuildTicks: (scale) => {
                scale.ticks = axisConfig.ticks
              },
              gridLines: {
                color: axisConfig.gridColor,
              },
            },
          ],
        },
        hover: {
          mode: 'nearest',
          intersect: false,
        },
        elements: {
          point: {
            hitRadius: 10,
          },
        },
      }
    },
    apiClient() {
      const hasHistoryDateRange =
        Array.isArray(this.historyDateRange) && this.historyDateRange.length === 2
      const hasInvalidHistoryDateRange =
        hasHistoryDateRange && !this.customDateValidation({ value: this.historyDateRange })

      if (hasInvalidHistoryDateRange) {
        return {
          get: async () => ({
            data: {
              paginated_result: [],
              ids: [],
            },
            metadata: {
              page: 1,
              total_count: 0,
            },
          }),
        }
      }

      let filters = []
      if (this.hasTmsColdChainAccess) {
        let temperatureData = null
        switch (this.selectedTemperature) {
          case 'Suhu 2':
            temperatureData = 11
            break
          case 'Suhu 3':
            temperatureData = 36
            break
          default:
            temperatureData = 12
            break
        }
        filters.push(`temperature_code eq ${temperatureData}`)

        let doorData = null
        switch (this.selectedDoor) {
          case 'Pintu 2':
            doorData = 5
            break
          default:
            doorData = 4
            break
        }
        filters.push(`door_code eq ${doorData}`)
      }

      if (
        Array.isArray(this.historyDateRange) 
        && this.historyDateRange.length === 2 
        && this.hasFeatureEditActivityTrackDOMGM
      ) {
        filters.push(`start_date eq ${this.historyDateRange[0]}`)
        filters.push(`end_date eq ${this.historyDateRange[1]}`)
      }

      return {
        get: Delivery.getDoTrackSensor(this.uuid, filters),
      }
    },
    disabledButtonHistoryTrip() {
      return !this.detailTripHistories?.data?.length
    },
  },
  watch: {
    filteredMarkers() {
      this.fitToBounds()
    },
    detailTripHistories: {
      handler() {
        this.$nextTick(() => {
          clearInterval(this.timer)
          this.isPlaying = false
          this.currentTime = 0
          this.liveMarker = null
          this._lastTooltipIndex = null
          this.tooltipVisible = false
          this.drawPolyline()
          this.fitToBounds()
          if (this.$refs.lineChart && this.$refs.lineChart.renderChart) {
            this.resetVisibleChartDatasets()
            this.$refs.lineChart.renderChart(this.chartData, this.chartOptions)
          }
          this.deliveryTableKey += 1
          const histories = this.detailTripHistories?.data?.filter((item) => !item.break) || []
          if (histories.length) {
            const startTime = new Date(histories[0].sentOn).getTime()
            const endTime = new Date(histories[histories.length - 1].sentOn).getTime()
            const totalDuration = (endTime - startTime) / 1000
            this.totalTime = totalDuration
          }
          EventBus.$emit &&
            EventBus.$emit('playback:update', {
              currentTime: this.currentTime,
              totalTime: this.totalTime,
              isPlaying: this.isPlaying,
              liveMarker: this.liveMarker,
            })
        })
      },
      deep: true,
      immediate: true,
    },
    selectedTemperature() {
      this.$nextTick(() => {
        clearInterval(this.timer)
        this.isPlaying = false
        this.currentTime = 0
        if (this.$refs.lineChart && this.$refs.lineChart.renderChart) {
          this.resetVisibleChartDatasets()
          this.$refs.lineChart.renderChart(this.chartData, this.chartOptions)
        }
        this.deliveryTableKey += 1
      })
    },
    selectedDoor() {
      this.$nextTick(() => {
        clearInterval(this.timer)
        this.isPlaying = false
        this.currentTime = 0
        if (this.$refs.lineChart && this.$refs.lineChart.renderChart) {
          this.resetVisibleChartDatasets()
          this.$refs.lineChart.renderChart(this.chartData, this.chartOptions)
        }
        this.deliveryTableKey += 1
      })
    },
    status: {
      handler() {
        this.initializeHistoryDateRange()
      },
      immediate: true,
    },
    uuid() {
      this.resetInfoSensorSnapshotCache()
    },
    pickupCompletedOn() {
      this.initializeHistoryDateRange()
    },
    dropCompletedOn() {
      this.initializeHistoryDateRange()
    },

    historyDateRange(value) {
      if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
        const allowedRange = this.getHistoryValidationRange()
        const selectedStart = moment(value[0])
        const selectedEnd = moment(value[1])

        if (allowedRange && selectedStart.isValid() && selectedEnd.isValid()) {
          let nextStart = value[0]
          let nextEnd = value[1]

          if (selectedStart.isBefore(allowedRange.allowedStart)) {
            const clampedStart = this.toLocalISOString(allowedRange.allowedStart.toDate())
            if (clampedStart) nextStart = clampedStart
          }

          if (selectedEnd.isAfter(allowedRange.allowedEnd)) {
            const clampedEnd = this.toLocalISOString(allowedRange.allowedEnd.toDate())
            if (clampedEnd) nextEnd = clampedEnd
          }

          if (nextStart !== value[0] || nextEnd !== value[1]) {
            this.historyDateRange = [nextStart, nextEnd]
            return
          }
        }
      }

      const isValid = this.customDateValidation({ value })
      if (
        isValid &&
        Array.isArray(value) &&
        value.length === 2 &&
        value[0] && 
        value[1]
      ) {
        this.$nextTick(() => {
          clearInterval(this.timer)
          this.isPlaying = false
          this.currentTime = 0
          this.liveMarker = null
          this._lastTooltipIndex = null
          this.tooltipVisible = false
          this.deliveryTableKey += 1
        })
        this.$emit('history-date-range-change', value)
      } else {
        this.polylines = []
        this.deliveryTableKey += 1
        this.chartRenderKey += 1
        this.$nextTick(() => {
          clearInterval(this.timer)
          this.isPlaying = false
          this.currentTime = 0
          this.liveMarker = null
          this._lastTooltipIndex = null
          this.tooltipVisible = false
        })
      }
    },
    mapCollapsed(value) {
      if (!this.sensorDataAnalysis || !this.isChart) return

      if (value) {
        this.ensureCollapsedChartLayout()
      } else {
        this.restoreDefaultChartLayout()
      }
    },
    sensorDataAnalysis(value) {
      if (value && this.mapCollapsed && this.isChart) {
        this.ensureCollapsedChartLayout()
      } else if (!value && this.mapCollapsed) {
        this.mapCollapsed = false
      }
    },
  },
  mounted() {
    this.fitToBounds()

    this._eventHandlers = {}

    this._eventHandlers.togglePlay = (payload) => {
      const desired = payload && typeof payload.isPlaying === 'boolean' ? payload.isPlaying : null
      if (desired === null) {
        if (this.isPlaying) {
          clearInterval(this.timer)
          this.isPlaying = false
        } else {
          this.startPlayback()
        }
      } else {
        if (desired && !this.isPlaying) this.startPlayback()
        if (!desired && this.isPlaying) {
          clearInterval(this.timer)
          this.isPlaying = false
        }
      }
    }

    this._eventHandlers.seek = (time) => {
      if (typeof time === 'number') {
        this.currentTime = time
        const histories = this.detailTripHistories?.data?.filter((i) => !i.break) || []
        if (histories.length) {
          const startTime = new Date(histories[0].sentOn).getTime()
          const currentTimestamp = startTime + this.currentTime * 1000
          let closest = histories[0]
          let minDiff = Math.abs(new Date(closest.sentOn).getTime() - currentTimestamp)
          for (let h of histories) {
            const diff = Math.abs(new Date(h.sentOn).getTime() - currentTimestamp)
            if (diff < minDiff) {
              minDiff = diff
              closest = h
            }
          }
          this.liveMarker = closest
        }
        EventBus.$emit &&
          EventBus.$emit('playback:update', {
            currentTime: this.currentTime,
            totalTime: this.totalTime,
            isPlaying: this.isPlaying,
            liveMarker: this.liveMarker,
          })
      }
    }

    this._eventHandlers.playSpeed = (speed) => {
      if (typeof speed === 'number' || typeof speed === 'string') this.playSpeed = Number(speed)
    }

    this._eventHandlers.locationTimeEditChange = () => {
      if (this.activeMarker) {
        this.closeInfo()
      }
    }

    EventBus.$on && EventBus.$on('togglePlay', this._eventHandlers.togglePlay)
    EventBus.$on && EventBus.$on('seek', this._eventHandlers.seek)
    EventBus.$on && EventBus.$on('playSpeed', this._eventHandlers.playSpeed)
    EventBus.$on &&
      EventBus.$on('location-time-edit-change', this._eventHandlers.locationTimeEditChange)
  },

  beforeDestroy() {
    clearInterval(this.timer)

    EventBus.$off && EventBus.$off('togglePlay', this._eventHandlers.togglePlay)
    EventBus.$off && EventBus.$off('seek', this._eventHandlers.seek)
    EventBus.$off && EventBus.$off('playSpeed', this._eventHandlers.playSpeed)
    EventBus.$off &&
      EventBus.$off('location-time-edit-change', this._eventHandlers.locationTimeEditChange)
  },

  methods: {
    getHistoryValidationRange() {
      if (
        !['Diambil', 'Terkirim', 'Sedang Transit'].includes(this.status) ||
        !this.pickupCompletedOn
      )
        return null

      const allowedStart = moment(this.pickupCompletedOn)
      let allowedEnd = moment(this.historyRangeEndDate)

      if (this.status === 'Diambil') {
        allowedEnd = moment(this.getPickupHistoryEndDate())
      } else if (this.hasFeatureEditActivityTrackDOMGM) {
        if (this.delivery?.shipment_type !== 'TRANSIT') {
          // Izinkan pemilihan tanggal sampai durasi asli + 3 hari
          allowedEnd.add(3, 'days')
        }
      } else if (this.status === 'Terkirim' && this.delivery?.shipment_type !== 'TRANSIT') {
        allowedEnd.add(72, 'hours')
      }

      const now = moment(this.currentDateTime || new Date())
      if (allowedEnd.isAfter(now)) {
        allowedEnd = moment(now)
      }

      return { allowedStart, allowedEnd }
    },
    customDateValidation({ value }) {
      const [startDate, endDate] = value
      if (!startDate || !endDate) return false
      
      const start = moment(startDate)
      const end = moment(endDate)
      if (!start.isValid() || !end.isValid()) return false
      
      const allowedRange = this.getHistoryValidationRange()
      if (!allowedRange) return false
      const { allowedStart, allowedEnd } = allowedRange

      const diffHours = end.diff(start, 'hours', true)
      if (
        start.isBefore(allowedStart) ||
        end.isAfter(allowedEnd) ||
        end.isBefore(start) ||
        diffHours > 72
      ) {
        return false
      }
      return true
    },
    formatIndexDate,
    getDuration,
    getDefaultVisibleChartDatasets() {
      return this.hasTmsColdChainAccess ? ['Kecepatan', 'Suhu', 'Pintu'] : ['Kecepatan']
    },
    resetVisibleChartDatasets() {
      this.visibleChartDatasets = this.getDefaultVisibleChartDatasets()
    },
    getEffectiveVisibleChartDatasets() {
      return this.visibleChartDatasets.length
        ? this.visibleChartDatasets
        : this.getDefaultVisibleChartDatasets()
    },
    buildAxisTicks(min, max, step) {
      if (max < min) return []

      const ticks = []
      let current = min

      while (current <= max) {
        ticks.push(Number(current.toFixed(2)))
        current += step
      }

      if (!ticks.length || ticks[ticks.length - 1] !== Number(max.toFixed(2))) {
        ticks.push(Number(max.toFixed(2)))
      }

      return [...new Set(ticks)].sort((a, b) => a - b)
    },
    getSpeedAxisStep(maxValue) {
      if (maxValue <= 20) return 5
      if (maxValue <= 60) return 10
      return 20
    },
    getTemperatureAxisStep(minValue, maxValue) {
      const range = maxValue - minValue

      if (range <= 10) return 1
      if (range <= 30) return 5
      return 10
    },
    getTemperatureDisplaySpan(minValue, maxValue) {
      const range = maxValue - minValue

      if (range <= 0) return 40
      return Math.max(range, 60)
    },
    clampValue(value, minValue, maxValue) {
      if (value < minValue) return minValue
      if (value > maxValue) return maxValue
      return value
    },
    getTemperatureAxisBounds(dataMin, dataMax) {
      const rawLowerLimit = this.delivery?.load_min_temperature
      const rawUpperLimit = this.delivery?.load_max_temperature
      const hasLowerLimit = rawLowerLimit !== null && rawLowerLimit !== undefined
      const hasUpperLimit = rawUpperLimit !== null && rawUpperLimit !== undefined

      let displayMin = dataMin
      let displayMax = dataMax

      if (hasLowerLimit) {
        displayMin = Math.min(dataMin, Number(rawLowerLimit))
      }

      if (hasUpperLimit) {
        displayMax = Math.max(dataMax, Number(rawUpperLimit))
      }

      if (displayMin > displayMax) {
        displayMin = Math.min(dataMin, dataMax)
        displayMax = Math.max(dataMin, dataMax)
      }

      if (displayMin === displayMax) {
        const pad = 1
        displayMin -= pad
        displayMax += pad
      }

      return {
        displayMin,
        displayMax,
      }
    },
    getSelectedTemperatureValue(item) {
      switch (this.selectedTemperature) {
        case 'Suhu 2':
          return item.temperature2
        case 'Suhu 3':
          return item.temperature3
        default:
          return item.temperature
      }
    },
    getVisibleChartGroups() {
      const allowedLabels = this.hasTmsColdChainAccess
        ? ['Kecepatan', 'Suhu', 'Pintu']
        : ['Kecepatan']
      const visibleLabels = this.getEffectiveVisibleChartDatasets()

      return allowedLabels.filter((label) => visibleLabels.includes(label))
    },
    toggleVisibleChartDatasets(labelsToToggle) {
      const currentVisible = this.getVisibleChartGroups()
      const orderedLabels = this.hasTmsColdChainAccess
        ? ['Kecepatan', 'Suhu', 'Pintu']
        : ['Kecepatan']
      const targetLabel = labelsToToggle.includes('Suhu') ? 'Suhu' : labelsToToggle[0]
      const isVisible = currentVisible.includes(targetLabel)

      const nextVisible = isVisible
        ? currentVisible.filter((label) => label !== targetLabel)
        : orderedLabels.filter((label) =>
            label === targetLabel ? true : currentVisible.includes(label)
          )

      // Sisakan minimal satu dataset agar chart tidak kosong total.
      this.visibleChartDatasets = nextVisible.length ? nextVisible : currentVisible
    },
    getChartScaleMetrics() {
      const data = this.detailTripHistories?.data?.filter((item) => !item.break) || []
      const speedValues = data.map((i) => Number(i.speed) || 0)
      let maxSpeed = Math.max(...speedValues, 0)
      const minSpeed = Math.min(...speedValues, 0)
      // Ensure at least a small range when all speeds are the same
      if (minSpeed === maxSpeed) {
        maxSpeed = Math.max(maxSpeed + 10, 10)
      }
      const speedStep = this.getSpeedAxisStep(maxSpeed)
      const speedTicks = this.buildAxisTicks(0, maxSpeed, speedStep)
      const visibleGroups = this.getVisibleChartGroups()
      const temperatureValues = data
        .map((item) => this.getSelectedTemperatureValue(item))
        .filter((value) => value !== null && value !== undefined && !isNaN(Number(value)))
        .map((value) => Number(value))
      const tempDataMin = temperatureValues.length ? Math.min(...temperatureValues) : 0
      const tempDataMax = temperatureValues.length ? Math.max(...temperatureValues) : 0
      const { displayMin: tempAxisMin, displayMax: tempAxisMax } = this.getTemperatureAxisBounds(
        tempDataMin,
        tempDataMax
      )
      const tempRange = tempAxisMax - tempAxisMin
      const tempStep = this.getTemperatureAxisStep(tempAxisMin, tempAxisMax)
      const tempDisplaySpan = this.getTemperatureDisplaySpan(tempAxisMin, tempAxisMax)
      const temperatureBaseTicks = this.buildAxisTicks(tempAxisMin, tempAxisMax, tempStep)

      let cursor = 0
      let tempScale = 1
      let tempStart = 0
      let tempEnd = 0
      let doorClosed = 0
      let doorOpen = 30
      let ticks = []
      const sectionSeparators = []

      if (visibleGroups.includes('Kecepatan')) {
        ticks.push(...speedTicks)
        cursor = maxSpeed
      }

      let tempTicks = []
      if (visibleGroups.includes('Suhu')) {
        tempStart = ticks.length ? cursor + 30 : 0
        if (ticks.length) sectionSeparators.push(tempStart)

        tempScale = tempRange > 0 ? tempDisplaySpan / tempRange : 1
        tempTicks = temperatureBaseTicks.map((tick) =>
          Number((tempStart + (tick - tempAxisMin) * tempScale).toFixed(2))
        )
        ticks.push(...tempTicks)
        tempEnd = Number((tempStart + tempDisplaySpan).toFixed(2))
        cursor = tempEnd
      }

      if (visibleGroups.includes('Pintu')) {
        doorClosed = ticks.length ? cursor + 30 : 0
        if (ticks.length) sectionSeparators.push(doorClosed)

        doorOpen = doorClosed + 30
        ticks.push(doorClosed, doorOpen)
        cursor = doorOpen
      }

      if (!ticks.length) ticks = [0]

      return {
        max: ticks[ticks.length - 1],
        stepSize: speedStep,
        ticks,
        tempScale,
        tempAxisMin,
        tempAxisMax,
        tempStart,
        tempEnd,
        doorClosed,
        doorOpen,
        tickCallback: (value) => {
          if (visibleGroups.includes('Kecepatan') && speedTicks.includes(value)) return `${value}`
          if (visibleGroups.includes('Suhu') && tempTicks.includes(value))
            return `${Number(((value - tempStart) / tempScale + tempAxisMin).toFixed(2))}`
          if (visibleGroups.includes('Pintu') && value === doorClosed) return 'Tutup'
          if (visibleGroups.includes('Pintu') && value === doorOpen) return 'Buka'
          return ''
        },
        gridColor: (context) => {
          const y = context.tick.value
          if (sectionSeparators.includes(y)) return '#ccc'
          return '#eee'
        },
      }
    },
    isLegendDatasetVisible(label) {
      return this.getEffectiveVisibleChartDatasets().includes(label)
    },
    getChartAxisConfig() {
      const scaleMetrics = this.getChartScaleMetrics()

      return {
        max: scaleMetrics.max,
        stepSize: scaleMetrics.stepSize,
        ticks: scaleMetrics.ticks,
        tickCallback: scaleMetrics.tickCallback,
        gridColor: scaleMetrics.gridColor,
      }
    },
    async fitToBounds() {
      if (!this.$refs.gmapRef) {
        return
      }
      const map = await this.$refs.gmapRef.$mapPromise
      const bounds = new this.google.maps.LatLngBounds()

      if (this.filteredMarkers.length) {
        this.filteredMarkers.forEach((m) => {
          const lat = Number(m.geolocation?.coordinate?.latitude)
          const lng = Number(m.geolocation?.coordinate?.longitude)
          if (this.isValidLatLng(lat, lng)) {
            bounds.extend({ lat, lng })
          }
        })
      }

      if (this.geofencePolygons.length) {
        this.geofencePolygons.forEach((polygon) => {
          polygon.paths.forEach((point) => {
            if (this.isValidLatLng(point.lat, point.lng)) {
              bounds.extend(point)
            }
          })
        })
      }

      if (this.geofenceCircles.length) {
        this.geofenceCircles.forEach((circle) => {
          const circleBounds = new this.google.maps.Circle({
            center: circle.center,
            radius: circle.radius,
          }).getBounds()

          if (circleBounds) bounds.union(circleBounds)
        })
      }

      if (this.detailTripHistories?.data?.length) {
        this.detailTripHistories.data.forEach((d) => {
          const lat = Number(d.latitude)
          const lng = Number(d.longitude)
          if (this.isValidLatLng(lat, lng)) {
            bounds.extend({ lat, lng })
          }
        })
      }

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds)
      }
    },
    async drawPolyline() {
      if (!this.detailTripHistories?.data?.length) return

      if (!this.$refs.gmapRef) {
        return
      }

      this.polylines = []

      let segment = []

      for (let i = 0; i < this.detailTripHistories.data.length; i++) {
        const point = this.detailTripHistories.data[i]

        if (point.break) {
          segment = []
          continue
        }

        const lat = Number(point.latitude)
        const lng = Number(point.longitude)

        if (!this.isValidLatLng(lat, lng)) continue

        segment.push({ ...point, latitude: lat, longitude: lng })

        if (segment.length >= 2) {
          const start = segment[segment.length - 2]
          const end = segment[segment.length - 1]

          let color
          if (start.speed >= 0 && start.speed <= 20) {
            color = '#f41d00'
          } else if (start.speed > 20 && start.speed <= 40) {
            color = '#f64a33'
          } else if (start.speed > 40 && start.speed <= 60) {
            color = '#ffa133'
          } else if (start.speed > 60 && start.speed <= 80) {
            color = '#71b099'
          } else if (start.speed > 80) {
            color = '#137c55'
          }

          this.polylines.push({
            path: [
              { lat: start.latitude, lng: start.longitude },
              { lat: end.latitude, lng: end.longitude },
            ],
            options: {
              strokeColor: color,
              strokeOpacity: 1.0,
              strokeWeight: 5,
            },
            start,
            end,
          })
        }
      }
    },
    getVehicleIcon(marker) {
      const iconBase = marker.vehicle?.vehicleType?.icon
      if (!iconBase) return require('@/assets/images/vehicles/car/car-dark-grey.png')
      if (this.status === 'Terkirim') {
        return require(`@/assets/images/vehicles/${iconBase}/${iconBase}-dark-grey.png`)
      }
      switch (marker.calculatedMotionStatus) {
        case 'P':
          return require(`@/assets/images/vehicles/${iconBase}/${iconBase}-red.png`)
        case 'A':
          return require(`@/assets/images/vehicles/${iconBase}/${iconBase}-blue.png`)
        case 'M':
          return require(`@/assets/images/vehicles/${iconBase}/${iconBase}-green.png`)
        case 'I':
          return require(`@/assets/images/vehicles/${iconBase}/${iconBase}-orange.png`)
        case 'O':
        case 'U':
          return require(`@/assets/images/vehicles/${iconBase}/${iconBase}-grey.png`)
        default:
          return require(`@/assets/images/vehicles/${iconBase}/${iconBase}-dark-grey.png`)
      }
    },
    hasRenderableValue(value) {
      return value === 0 || (value !== null && value !== undefined && value !== '')
    },
    resetInfoSensorSnapshotCache() {
      this.infoSensorSnapshotCache = {}
      this.infoSensorSnapshotRequests = {}
    },
    getInfoSensorCompletedOn(locationType) {
      return locationType === 'ORIGIN'
        ? this.delivery?.delivery_order_activity?.pickup?.completed_on
        : this.delivery?.delivery_order_activity?.drop?.completed_on
    },
    buildInfoSensorFilters(completedOn) {
      const endDate = completedOn instanceof Date ? completedOn : new Date(completedOn)
      if (isNaN(endDate.getTime())) return []

      const startDate = new Date(endDate.getTime() - 5 * 60 * 1000)
      const startDateFilter = this.toLocalISOString(startDate)
      const endDateFilter = this.toLocalISOString(endDate)

      if (!startDateFilter || !endDateFilter) return []

      return [`start_date eq ${startDateFilter}`, `end_date eq ${endDateFilter}`]
    },
    async fetchInfoSensor(filters) {
      if (!this.uuid || !filters?.length) return null

      try {
        const response = await Delivery.getDoTrackSensor(
          this.uuid,
          filters
        )({
          limit: 1,
          page: 1,
        })

        return response?.data?.paginated_result?.[0] || null
      } catch (error) {
        return null
      }
    },
    async getInfoSensorSnapshot(locationType, options = {}) {
      const { useCache = true } = options
      const completedOn = this.getInfoSensorCompletedOn(locationType)
      const filters = this.buildInfoSensorFilters(completedOn)

      if (!this.uuid || !filters.length) return null

      if (!useCache) {
        return await this.fetchInfoSensor(filters)
      }

      const cacheKey = `${locationType}:${this.uuid}:${completedOn}`

      if (Object.prototype.hasOwnProperty.call(this.infoSensorSnapshotCache, cacheKey)) {
        return this.infoSensorSnapshotCache[cacheKey]
      }

      if (this.infoSensorSnapshotRequests[cacheKey]) {
        return await this.infoSensorSnapshotRequests[cacheKey]
      }

      const request = this.fetchInfoSensor(filters)
        .then((snapshot) => {
          this.infoSensorSnapshotCache[cacheKey] = snapshot
          return snapshot
        })
        .finally(() => {
          delete this.infoSensorSnapshotRequests[cacheKey]
        })

      this.infoSensorSnapshotRequests[cacheKey] = request

      return await request
    },
    buildOriginDestinationInfoFields({ locationType, marker, screenWidth, sensorSnapshot }) {
      const isOrigin = locationType === 'ORIGIN'
      const isMobileLayout = screenWidth < 890
      const activity = isOrigin
        ? this.delivery?.delivery_order_activity?.pickup
        : this.delivery?.delivery_order_activity?.drop

      const actualCompletedOn = this.getInfoSensorCompletedOn(locationType)
      const actualLabel = isOrigin ? 'Aktual Diambil' : 'Aktual Kirim'
      const targetPrimaryLabel = isOrigin ? 'Target Diambil' : 'Target Terkirim'
      const targetOn = isOrigin
        ? this.delivery?.expected_pickup_on
        : this.delivery?.expected_delivered_on

      const formatDateTime = (value) =>
        value ? `${this.formatIndexDate(value).date}, ${this.formatIndexDate(value).time}` : '-'

      const actualValue = formatDateTime(actualCompletedOn)
      const targetValue = formatDateTime(targetOn)
      const vehiclePlate = this.delivery?.fleet_task?.vehicle?.license_plate
      const coordinate = isOrigin
        ? this.delivery?.origin_address?.geolocation?.coordinate
        : this.delivery?.destination_address?.geolocation?.coordinate

      const hasTemperature = this.hasRenderableValue(sensorSnapshot?.temperature)
      const hasAcc = sensorSnapshot?.acc === 0 || sensorSnapshot?.acc === 1
      const hasRefrigerator =
        sensorSnapshot?.refrigerator === 0 || sensorSnapshot?.refrigerator === 1
      const hasDoor = sensorSnapshot?.door === 0 || sensorSnapshot?.door === 1
      const showVehiclePlate =
        !!actualCompletedOn &&
        this.hasFeatureEditActivityTrackDOMGM &&
        this.hasRenderableValue(vehiclePlate)

      console.log(showVehiclePlate)

      const locationFields = [
        {
          label: 'Nama Lokasi',
          value: marker?.name,
          isShow: this.hasRenderableValue(marker?.name),
        },
        {
          label: screenWidth < 890 ? 'Alamat' : 'Detail Lokasi',
          value: marker?.full_name,
          isShow: this.hasRenderableValue(marker?.full_name),
        },
        {
          label: 'Latitude',
          value: coordinate?.latitude,
          column: 'half',
          isShow: this.hasRenderableValue(coordinate?.latitude),
        },
        {
          label: 'Longitude',
          value: coordinate?.longitude,
          column: 'half',
          isShow: this.hasRenderableValue(coordinate?.longitude),
        },
      ]

      if (isMobileLayout) {
        const fields = [
          {
            label: 'Masuk Geolokasi',
            value: formatDateTime(activity?.geofence_in),
            isShow: Boolean(activity?.geofence_in && this.hasFeatureEditActivityTrackDOMGM),
          },
          {
            label: actualLabel,
            value: actualValue,
          },
          {
            label: targetOn ? `(Target: ${targetValue})` : '-',
            value: '',
            showValue: false,
            isShow: Boolean(targetOn && this.hasFeatureEditActivityTrackDOMGM),
          },
          {
            label: 'Keluar Geolokasi',
            value: formatDateTime(activity?.geofence_out),
            isShow: Boolean(activity?.geofence_out && this.hasFeatureEditActivityTrackDOMGM),
          },
          {
            divider: true,
            isShow:
              Boolean(activity?.geofence_in && this.hasFeatureEditActivityTrackDOMGM) ||
              Boolean(targetOn && this.hasFeatureEditActivityTrackDOMGM) ||
              Boolean(activity?.geofence_out && this.hasFeatureEditActivityTrackDOMGM),
          },
          {
            label: 'Plat Nomor',
            value: vehiclePlate,
            isShow: showVehiclePlate,
          },
          {
            label: 'Suhu',
            value: hasTemperature ? `${sensorSnapshot.temperature}°C` : '-',
            column: 'half',
            isShow: this.hasTmsColdChainAccess && hasTemperature,
          },
          {
            label: 'Refrigerator',
            value: sensorSnapshot?.refrigerator === 1 ? 'ON' : 'OFF',
            column: 'half',
            isShow: this.hasTmsColdChainAccess && hasRefrigerator,
          },
          {
            label: 'Pintu',
            value: sensorSnapshot?.door === 1 ? 'Buka' : 'Tutup',
            column: 'half',
            isShow: this.hasTmsColdChainAccess && hasDoor,
          },
          {
            label: 'ACC',
            value: sensorSnapshot?.acc === 1 ? 'Nyala' : 'Mati',
            column: 'half',
            isShow: this.hasTmsColdChainAccess && hasAcc,
          },
          {
            divider: true,
            isShow:
              showVehiclePlate ||
              (this.hasTmsColdChainAccess &&
                (hasTemperature || hasRefrigerator || hasDoor || hasAcc)),
          },
          ...locationFields,
        ]

        return fields
      }

      const showTargetPrimary = this.hasRenderableValue(targetOn)

      const fields = [
        {
          label: targetPrimaryLabel,
          value: targetValue,
          column: 'half',
          isShow: showTargetPrimary,
        },
        {
          label: actualLabel,
          value: actualValue,
          column: showTargetPrimary ? 'half' : undefined,
        },
        {
          divider: true,
        },
        {
          label: 'Plat Nomor',
          value: vehiclePlate,
          isShow: showVehiclePlate,
        },
        {
          label: 'Suhu',
          value: hasTemperature ? `${sensorSnapshot.temperature}°C` : '-',
          column: 'half',
          isShow: this.hasTmsColdChainAccess && hasTemperature,
        },
        {
          label: 'Refrigerator',
          value: sensorSnapshot?.refrigerator === 1 ? 'ON' : 'OFF',
          column: 'half',
          isShow: this.hasTmsColdChainAccess && hasRefrigerator,
        },
        {
          label: 'Pintu',
          value: sensorSnapshot?.door === 1 ? 'Buka' : 'Tutup',
          column: 'half',
          isShow: this.hasTmsColdChainAccess && hasDoor,
        },
        {
          label: 'ACC',
          value: sensorSnapshot?.acc === 1 ? 'Nyala' : 'Mati',
          column: 'half',
          isShow: this.hasTmsColdChainAccess && hasAcc,
        },
        {
          divider: true,
          isShow:
            showVehiclePlate ||
            (this.hasTmsColdChainAccess &&
              (hasTemperature || hasRefrigerator || hasDoor || hasAcc)),
        },
        ...locationFields,
        {
          label: 'Masuk Geolokasi',
          value: formatDateTime(activity?.geofence_in),
          column: 'half',
          isShow: Boolean(activity?.geofence_in && this.hasFeatureEditActivityTrackDOMGM),
        },
        {
          label: 'Keluar Geolokasi',
          value: formatDateTime(activity?.geofence_out),
          column: 'half',
          isShow: Boolean(activity?.geofence_out && this.hasFeatureEditActivityTrackDOMGM),
        },
      ]

      console.log(fields)

      return fields
    },
    async openInfo(marker, type) {
      const screenWidth = window.innerWidth
      if (type === 'stoppage') {
        marker.type = 'STOP'
        marker.geolocation = {
          coordinate: {
            latitude: marker.stopLat,
            longitude: marker.stopLong,
          },
        }
        const address = await GetAddressData(marker.stopLat, marker.stopLong)
        marker.full_name = address.display_name
      } else if (type === 'polyline') {
        marker.type = 'POLYLINE'

        const startLat = Number(marker.start.latitude)
        const startLng = Number(marker.start.longitude)
        const endLat = Number(marker.end.latitude)
        const endLng = Number(marker.end.longitude)

        const midLat = (startLat + endLat) / 2
        const midLng = (startLng + endLng) / 2

        marker.geolocation = {
          coordinate: {
            latitude: midLat,
            longitude: midLng,
          },
        }

        const address = await GetAddressData(startLat, startLng)
        marker.full_name = address.display_name
        marker.speed = marker.start.speed
      }

      const baseInfo = {
        title: '',
        fields: [],
        location: marker.full_name,
        position: {
          lat: Number(marker.geolocation.coordinate.latitude),
          lng: Number(marker.geolocation.coordinate.longitude),
        },
      }

      const shouldFetchSensor =
        this.hasTmsColdChainAccess && ['ORIGIN', 'DESTINATION'].includes(marker.type)
      const sensorSnapshot = shouldFetchSensor
        ? await this.getInfoSensorSnapshot(marker.type, { useCache: !this.isLiveview })
        : null

      const infoConfig = {
        ORIGIN: () => ({
          title: 'Lokasi Ambil',
          fields: this.buildOriginDestinationInfoFields({
            locationType: 'ORIGIN',
            marker,
            screenWidth,
            sensorSnapshot,
          }),
          type: 'ORIGIN',
        }),
        TRANSIT: () => {
          if (marker.status === 'UNCOMPLETED') {
            return {
              title: 'Lokasi Transit',
              fields: [{ label: 'Detail Lokasi', value: marker.full_name }],
              type: 'TRANSIT',
            }
          }

          return {
            title: 'Lokasi Transit',
            fields: [
              {
                label: 'Telah ditransitkan pada',
                value: `${
                  this.formatIndexDate(marker.delivery_order_activity.drop.completed_on).date
                }, ${this.formatIndexDate(marker.delivery_order_activity.drop.completed_on).time}`,
              },
              { divider: true },
              { label: 'Detail Lokasi', value: marker.full_name },
            ],
            type: 'TRANSIT',
          }
        },
        DESTINATION: () => ({
          title: 'Lokasi Kirim',
          fields: this.buildOriginDestinationInfoFields({
            locationType: 'DESTINATION',
            marker,
            screenWidth,
            sensorSnapshot,
          }),
          type: 'DESTINATION',
        }),
        STOP: () => ({
          title: 'Berhenti Kendaraan',
          fields: [
            {
              label: 'Waktu',
              value: `${this.getDuration(marker.startDate, marker.endDate).range}`,
            },
            {
              label: 'Durasi Berhenti',
              value: `${this.getDuration(marker.startDate, marker.endDate).duration}`,
            },
            { divider: true },
            {
              label: 'Detail Lokasi',
              value: marker.full_name,
            },
          ],
          type: 'STOP',
        }),
        POLYLINE: () => ({
          title: 'Kecepatan',
          fields: [
            {
              label: 'Waktu',
              value: `${this.formatIndexDate(marker.start.sentOn).date}, ${
                this.formatIndexDate(marker.start.sentOn).time
              }`,
            },
            {
              label: 'Kecepatan',
              value: `${marker.speed} Km/Jam`,
            },
            { divider: true },
            {
              label: 'Detail Lokasi',
              value: marker.full_name,
            },
          ],
          type: 'POLYLINE',
        }),
      }

      const configFn = infoConfig[marker.type] || (() => ({}))
      this.markers = { ...baseInfo, ...configFn() }
      if (screenWidth < 890) {
        this.$emit('marker-selected', this.markers)
        return
      }
      this.activeMarker = true
    },
    closeInfo() {
      this.activeMarker = false
    },
    isValidLatLng(lat, lng) {
      return typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)
    },
    togglePopup() {
      this.showPopup = !this.showPopup
    },
    closePopup() {
      this.showPopup = false
    },
    getTransitDateRange() {
      if (this.delivery?.shipment_type !== 'TRANSIT') return null

      const transits = this.delivery?.transits || []
      if (transits.length === 0) return null

      const firstLeg = transits[0]
      const firstPickup = firstLeg?.delivery_order_activity?.pickup

      let latestLeg = null
      let latestActivityType = null

      for (let i = transits.length - 1; i >= 0; i--) {
        const leg = transits[i]
        const activity = leg.delivery_order_activity
        if (activity?.drop?.geofence_out || activity?.drop?.completed_on) {
          latestLeg = leg
          latestActivityType = 'drop'
          break
        }
        if (activity?.pickup?.geofence_in || activity?.pickup?.completed_on) {
          latestLeg = leg
          latestActivityType = 'pickup'
          break
        }
      }

      if (!latestLeg) return null

      let start, end

      if (this.status === 'Terkirim' && latestLeg === transits[transits.length - 1]) {
        // Case 5: FO-3 selesai drop barang (DELIVERED)
        start = firstPickup?.geofence_in || firstPickup?.completed_on
        end =
          latestLeg?.delivery_order_activity?.drop?.geofence_out ||
          latestLeg?.delivery_order_activity?.drop?.completed_on
      } else {
        // Case 1-4
        start = firstPickup?.geofence_in || firstPickup?.completed_on
        if (latestActivityType === 'drop') {
          const drop = latestLeg?.delivery_order_activity?.drop
          end = drop?.geofence_out || drop?.completed_on
        } else {
          const pickup = latestLeg?.delivery_order_activity?.pickup
          end = pickup?.geofence_out || pickup?.completed_on
        }
      }

      return { start, end }
    },
    initializeHistoryDateRange() {
      if (
        !['Diambil', 'Terkirim', 'Sedang Transit'].includes(this.status) ||
        !this.pickupCompletedOn
      )
        return

      const startDate = this.toLocalISOString(this.pickupCompletedOn)
      let endReference = new Date(this.historyRangeEndDate)

      if (this.delivery?.shipment_type !== 'TRANSIT') {
        const threeDaysAfterStart = new Date(this.pickupCompletedOn)
        threeDaysAfterStart.setDate(threeDaysAfterStart.getDate() + 3)

        if (endReference > threeDaysAfterStart) {
          endReference = threeDaysAfterStart
        }
      }

      const endDate = this.toLocalISOString(endReference)
      if (!startDate || !endDate) return

      if (Array.isArray(this.historyDateRange) && this.historyDateRange.length) {
        const isBaseDataChanged =
          !this.initialHistoryDateRange ||
          this.initialHistoryDateRange[0] !== startDate ||
          this.initialHistoryDateRange[1] !== endDate

        if (!isBaseDataChanged) return
        if (this.status !== 'Terkirim' && this.historyDateRangeChanged) return
      }

      this.historyDateRange = [startDate, endDate]
      this.initialHistoryDateRange = [startDate, endDate]
      this.historyDateRangeKey += 1
    },
    toLocalISOString(value) {
      const date = value instanceof Date ? value : new Date(value)
      if (isNaN(date.getTime())) return null

      const tzOffset = -date.getTimezoneOffset()
      const sign = tzOffset >= 0 ? '+' : '-'
      const pad = (n) => `${Math.floor(Math.abs(n))}`.padStart(2, '0')
      const hours = pad(tzOffset / 60)
      const minutes = pad(tzOffset % 60)

      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.000${sign}${hours}:${minutes}`
    },
    getPickupHistoryEndDate() {
      const nowReference = new Date(this.currentDateTime || new Date())
      const pickupReference = new Date(this.pickupCompletedOn)

      if (isNaN(nowReference.getTime())) return new Date()
      if (isNaN(pickupReference.getTime())) return nowReference

      const threeDaysAfterPickup = new Date(pickupReference)
      threeDaysAfterPickup.setHours(threeDaysAfterPickup.getHours() + 72)

      return nowReference > threeDaysAfterPickup ? threeDaysAfterPickup : nowReference
    },
    disabledHistoryDate(date) {
      if (
        !['Diambil', 'Terkirim', 'Sedang Transit'].includes(this.status) ||
        !this.pickupCompletedOn
      )
        return false

      const start = new Date(this.pickupCompletedOn)
      let end = new Date(this.historyRangeEndDate)

      if (this.status === 'Diambil') {
        end = this.getPickupHistoryEndDate()
      } else if (this.hasFeatureEditActivityTrackDOMGM) {
        if (this.delivery?.shipment_type !== 'TRANSIT') {
          // Izinkan pemilihan tanggal sampai durasi asli + 3 hari
          end.setDate(end.getDate() + 3)
        }
      } else {
        if (this.status === 'Terkirim' && this.delivery?.shipment_type !== 'TRANSIT') {
          end.setHours(end.getHours() + 72)
        }
      }

      // Cap end date to currentDateTime if it exceeds
      const now = new Date(this.currentDateTime || new Date())
      if (end > now) {
        end = new Date(now)
      }

      if (isNaN(start.getTime()) || isNaN(end.getTime())) return false

      const startDay = new Date(start)
      startDay.setHours(0, 0, 0, 0)

      const endDay = new Date(end)
      endDay.setHours(23, 59, 59, 999)

      const selectedDay = new Date(date)
      return selectedDay < startDay || selectedDay > endDay
    },
    formatAvailableDateTime(value) {
      const date = value instanceof Date ? value : new Date(value)
      if (isNaN(date.getTime())) return ''

      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'Mei',
        'Jun',
        'Jul',
        'Agu',
        'Sep',
        'Okt',
        'Nov',
        'Des',
      ]
      const pad = (n) => `${n}`.padStart(2, '0')

      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()} ${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`
    },
    showBanner() {
      if (this.status === 'Dijalankan' && this.delivery?.shipment_type !== 'TRANSIT') {
        return true
      }

      if (
        (this.status === 'Dijalankan' || this.status === 'Dijadwalkan') &&
        this.delivery?.shipment_type === 'TRANSIT'
      ) {
        const hasCompletedTransit = this.delivery.transits?.some((t) => t.status === 'COMPLETED')
        return !hasCompletedTransit
      }

      return false
    },
    toggleView(params) {
      this.isLiveview = params === 'live'
      if (params === 'live') {
        this.sensorDataAnalysis = false
        this.tooltipVisible = false
      } else {
        this.initializeHistoryDateRange()
      }
      const histories = this.detailTripHistories?.data?.filter((item) => !item.break) || []

      const startTime = new Date(histories[0]?.sentOn).getTime()
      const endTime = new Date(histories[histories.length - 1]?.sentOn).getTime()
      const totalDuration = (endTime - startTime) / 1000

      this.totalTime = totalDuration
      this.$emit('isLiveview', params)
    },
    togglePlay() {
      this.isPlaying = !this.isPlaying
      if (this.isPlaying) {
        this.startPlayback()
      } else {
        clearInterval(this.timer)
        this.timer = null
        this._lastTooltipIndex = null
        EventBus.$emit &&
          EventBus.$emit('playback:update', {
            currentTime: this.currentTime,
            totalTime: this.totalTime,
            isPlaying: this.isPlaying,
            liveMarker: this.liveMarker,
          })
      }
    },
    startPlayback() {
      clearInterval(this.timer)

      const histories = this.detailTripHistories?.data?.filter((item) => !item.break) || []
      if (histories.length === 0) return

      const startTime = new Date(histories[0].sentOn).getTime()
      const endTime = new Date(histories[histories.length - 1].sentOn).getTime()
      const totalDuration = (endTime - startTime) / 1000
      this.totalTime = totalDuration

      if (this.currentTime >= this.totalTime) {
        this.currentTime = 0
      }

      this.isPlaying = true

      if (histories.length) {
        const currentTimestamp = startTime + this.currentTime * 1000
        let closest = histories[0]
        let minDiff = Math.abs(new Date(closest.sentOn).getTime() - currentTimestamp)
        for (let h of histories) {
          const diff = Math.abs(new Date(h.sentOn).getTime() - currentTimestamp)
          if (diff < minDiff) {
            minDiff = diff
            closest = h
          }
        }
        this.liveMarker = closest

        if (this.isChart && this.$refs.lineChart) {
          const originalIndex = histories.findIndex((h) => h.sentOn === closest.sentOn)
          if (originalIndex !== -1 && originalIndex !== this._lastTooltipIndex) {
            this._lastTooltipIndex = originalIndex
            this.updateTooltipForChartIndex(originalIndex, closest)
          }
        }
      }

      EventBus.$emit &&
        EventBus.$emit('playback:update', {
          currentTime: this.currentTime,
          totalTime: this.totalTime,
          isPlaying: this.isPlaying,
          liveMarker: this.liveMarker,
        })

      const intervalMs = 100
      this.timer = setInterval(() => {
        if (!this.isPlaying) return
        const delta = (intervalMs / 1000) * (Number(this.playSpeed) || 1)
        this.currentTime += delta
        if (this.currentTime >= this.totalTime) {
          this.currentTime = this.totalTime
          this.isPlaying = false
          clearInterval(this.timer)
          this.timer = null
          this._lastTooltipIndex = null
        }

        if (histories.length) {
          const currentTimestamp = startTime + this.currentTime * 1000
          let closest = histories[0]
          let minDiff = Math.abs(new Date(closest.sentOn).getTime() - currentTimestamp)
          for (let h of histories) {
            const diff = Math.abs(new Date(h.sentOn).getTime() - currentTimestamp)
            if (diff < minDiff) {
              minDiff = diff
              closest = h
            }
          }
          this.liveMarker = closest

          if (this.isChart && this.$refs.lineChart) {
            const originalIndex = histories.findIndex((h) => h.sentOn === closest.sentOn)
            if (originalIndex !== -1 && originalIndex !== this._lastTooltipIndex) {
              this._lastTooltipIndex = originalIndex
              this.updateTooltipForChartIndex(originalIndex, closest)
            }
          }
        }

        EventBus.$emit &&
          EventBus.$emit('playback:update', {
            currentTime: this.currentTime,
            totalTime: this.totalTime,
            isPlaying: this.isPlaying,
            liveMarker: this.liveMarker,
          })
      }, intervalMs)
    },
    rewind10() {
      this.currentTime = Math.max(0, this.currentTime - 10)
    },
    forward10() {
      this.currentTime = Math.min(this.totalTime, this.currentTime + 10)
    },
    formatTime(sec) {
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      const s = Math.floor(sec % 60)

      if (h > 0) {
        return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      }

      return `${m}:${s.toString().padStart(2, '0')}`
    },
    startDrag(event) {
      this.isDragging = true
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
      document.addEventListener('touchmove', this.onDrag)
      document.addEventListener('touchend', this.stopDrag)
      this.updateDragPosition(event)
    },
    onDrag(event) {
      if (!this.isDragging) return
      this.updateDragPosition(event)
    },
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
      document.removeEventListener('touchmove', this.onDrag)
      document.removeEventListener('touchend', this.stopDrag)
    },
    updateDragPosition(event) {
      const bar = this.$refs.trackBar
      const rect = bar.getBoundingClientRect()
      const clientX = event.touches ? event.touches[0].clientX : event.clientX
      let offsetX = clientX - rect.left

      if (offsetX < 0) offsetX = 0
      if (offsetX > rect.width) offsetX = rect.width

      const percent = offsetX / rect.width
      this.currentTime = percent * this.totalTime
      const histories = this.detailTripHistories?.data?.filter((item) => !item.break) || []
      if (histories.length) {
        const startTime = new Date(histories[0].sentOn).getTime()
        const currentTimestamp = startTime + this.currentTime * 1000
        const currentPosition = histories
          .slice()
          .reverse()
          .find((item) => new Date(item.sentOn).getTime() <= currentTimestamp)

        let temperatureData = null
        switch (this.selectedTemperature) {
          case 'Suhu 2':
            temperatureData = currentPosition.temperature2
            break
          case 'Suhu 3':
            temperatureData = currentPosition.temperature3
            break
          default:
            temperatureData = currentPosition.temperature
            break
        }

        let doorData = null
        switch (this.selectedDoor) {
          case 'Pintu 2':
            doorData = currentPosition.door2
            break
          default:
            doorData = currentPosition.door1
            break
        }

        if (currentPosition) {
          this.liveMarker = {
            latitude: currentPosition.latitude,
            longitude: currentPosition.longitude,
            direction: currentPosition.direction,
            speed: currentPosition.speed,
            sentOn: currentPosition.sentOn,
            temperature: temperatureData,
            door1: doorData,
          }

          const originalIndex = histories.findIndex((h) => h.sentOn === currentPosition.sentOn)
          if (originalIndex !== -1) this.updateTooltipForChartIndex(originalIndex, currentPosition)
        }
      }
    },
    toggleAnalysis(params) {
      this.isChart = params === 'chart'
      if (params === 'table') {
        this.tooltipVisible = false
      } else if (this.mapCollapsed && this.sensorDataAnalysis) {
        this.ensureCollapsedChartLayout()
      }
    },
    ensureCollapsedChartLayout() {
      this.chartRenderKey += 1
      this.scheduleChartResize()
    },
    restoreDefaultChartLayout() {
      this.chartRenderKey += 1
      this.scheduleChartResize()
    },
    scheduleChartResize() {
      this.$nextTick(() => {
        const applyResize = () => {
          const chart = this.getChartInstance()
          if (!chart) return

          if (typeof chart.resize === 'function') {
            chart.resize()
          }

          if (typeof chart.update === 'function') {
            chart.update(0)
          }
        }

        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => requestAnimationFrame(applyResize))
          return
        }

        setTimeout(applyResize, 0)
      })
    },
    val(v) {
      if (v === 0) return 0
      return v !== null && v !== undefined && v !== '' ? v : '-'
    },
    formatDateTime() {
      const sentOn = this.liveMarker?.sentOn

      if (!sentOn) return '-'

      const f = this.formatIndexDate(sentOn)

      if (!f) return '-'

      return `${this.val(f.date)}, ${this.val(f.time)}`
    },
    handleDownload() {
      let filters = []
      if (this.hasTmsColdChainAccess) {
        let temperatureData = null
        switch (this.selectedTemperature) {
          case 'Suhu 2':
            temperatureData = 11
            break
          case 'Suhu 3':
            temperatureData = 36
            break
          default:
            temperatureData = 12
            break
        }
        filters.push(`temperature_code eq ${temperatureData}`)
        let doorData = null
        switch (this.selectedDoor) {
          case 'Pintu 2':
            doorData = 5
            break
          default:
            doorData = 4
            break
        }
        filters.push(`door_code eq ${doorData}`)
      }

      if (Array.isArray(this.historyDateRange) && this.historyDateRange.length === 2) {
        filters.push(`start_date eq ${this.historyDateRange[0]}`)
        filters.push(`end_date eq ${this.historyDateRange[1]}`)
      }

      const params = { format: 'excel' }
      if (filters.length > 0) {
        params.filters = filters.join(', ')
      }

      window.open(
        `${process.env.VUE_APP_API_ORDER_URL}/delivery-order/track/${
          this.uuid
        }/sensor?${new URLSearchParams(params).toString()}`,
        '_self'
      )
    },
    updateTooltipForChartIndex(originalIndex, currentPosition) {
      const chartComp = this.$refs.lineChart
      const chart = chartComp && (chartComp._chart || (chartComp.$data && chartComp.$data._chart))
      if (!chart || !chart.getDatasetMeta) {
        this.tooltipVisible = false
        this.showVerticalLine = false
        return
      }

      const primaryDatasetIndex = this.getPrimaryVisibleDatasetIndex(chart)
      if (primaryDatasetIndex === -1) {
        this.tooltipVisible = false
        this.showVerticalLine = false
        return
      }

      const meta = chart.getDatasetMeta(primaryDatasetIndex)
      const point = meta && meta.data && meta.data[originalIndex]
      if (!point) {
        this.tooltipVisible = false
        this.showVerticalLine = false
        return
      }

      const model = point._model || point
      const pointX = model.x

      const chartRect = chart.canvas.getBoundingClientRect()
      const containerRect = this.$el.getBoundingClientRect()

      const posX = Math.round(pointX + (chartRect.left - containerRect.left) + 10)
      const posY = Math.round(chartRect.top - containerRect.top + 30)

      this.verticalX = Math.round(pointX + (chartRect.left - containerRect.left))
      this.verticalLineTop = Math.round(chartRect.top - containerRect.top + 6)
      this.verticalLineHeight = chart.chartArea.bottom - chart.chartArea.top
      this.showVerticalLine = true

      const f = this.formatIndexDate(currentPosition.sentOn)
      this.tooltipTitle = `${f.date} ${f.time}`

      const items = []
      if (this.isLegendDatasetVisible('Kecepatan')) {
        items.push({
          color: '#8F49DE',
          label: 'Kecepatan',
          value: `${currentPosition.speed} Km/Jam`,
        })
      }

      if (this.hasTmsColdChainAccess && this.isLegendDatasetVisible('Suhu')) {
        let tempVal = null
        switch (this.selectedTemperature) {
          case 'Suhu 2':
            tempVal = currentPosition.temperature2
            break
          case 'Suhu 3':
            tempVal = currentPosition.temperature3
            break
          default:
            tempVal = currentPosition.temperature
        }

        items.push({
          color: '#FF8A00',
          label: 'Suhu',
          value: tempVal == null ? '-' : `${tempVal}°C`,
        })
      }

      if (this.hasTmsColdChainAccess && this.isLegendDatasetVisible('Pintu')) {
        const doorVal =
          this.selectedDoor === 'Pintu 2' ? currentPosition.door2 : currentPosition.door1

        let doorLabel = '-'

        if (doorVal === 1) {
          doorLabel = 'Buka'
        } else if (doorVal === 0) {
          doorLabel = 'Tutup'
        }

        items.push({
          color: '#4990E1',
          label: 'Pintu',
          value: doorLabel,
        })
      }

      this.tooltipItems = items

      this.$nextTick(() => {
        const adjusted = this.adjustTooltipPosition(posX, posY, this.verticalX)
        this.tooltipPos = adjusted
        this.tooltipVisible = true
      })
    },
    getChartInstance() {
      const comp = this.$refs.lineChart
      if (!comp) return null

      return comp._chart || comp.chart || (comp.$data && comp.$data._chart) || null
    },
    getPrimaryVisibleDatasetIndex(chart = this.getChartInstance()) {
      if (!chart?.data?.datasets?.length) return -1

      return chart.data.datasets.findIndex((dataset) => {
        if (dataset.hidden) return false

        if (dataset.label === 'Kecepatan') return this.isLegendDatasetVisible('Kecepatan')
        if (dataset.label === 'Suhu') return this.isLegendDatasetVisible('Suhu')
        if (dataset.label === 'Pintu') return this.isLegendDatasetVisible('Pintu')

        return false
      })
    },
    adjustTooltipPosition(origX, origY, verticalX) {
      const containerRect = this.$el.getBoundingClientRect()
      const containerRight = containerRect.right

      const getTooltipWidth = () => {
        const el = this.$el.querySelector('.custom-tooltip')
        if (el) return el.offsetWidth
        return 200
      }

      const getTooltipHeight = () => {
        const el = this.$el.querySelector('.custom-tooltip')
        if (el) return el.offsetHeight
        return 120
      }

      const tooltipWidth = getTooltipWidth()
      const tooltipHeight = getTooltipHeight()
      const margin = 10

      let left = verticalX + margin
      let top = origY

      const rightEdge = containerRect.left + left + tooltipWidth
      if (rightEdge > containerRight - 10) {
        left = verticalX - tooltipWidth - margin
        if (left < 10) left = 10
      }

      if (this.mapCollapsed) {
        const analysisEl = this.$el.querySelector('.analysis-container')
        const analysisRect = analysisEl?.getBoundingClientRect()

        if (analysisRect) {
          top = Math.round(
            analysisRect.top - containerRect.top + (analysisRect.height - tooltipHeight) / 2
          )
        } else {
          top = Math.round((containerRect.height - tooltipHeight) / 2)
        }
      }

      const minTop = 10
      const maxTop = Math.max(minTop, containerRect.height - tooltipHeight - 10)
      if (top < minTop) top = minTop
      if (top > maxTop) top = maxTop

      return {
        x: left,
        y: top,
      }
    },
    handleHoverTooltip(tooltipModel) {
      if (this.isPlaying) return

      if (tooltipModel.opacity === 0) {
        this.tooltipVisible = false
        this.showVerticalLine = false
        return
      }

      const chart = this.getChartInstance()
      if (!chart || !chart.canvas) {
        this.tooltipVisible = false
        this.showVerticalLine = false
        return
      }

      const dp = tooltipModel.dataPoints?.[0]
      if (!dp) return

      const index = dp.index
      const pointData = this.detailTripHistories.data.filter((i) => !i.break)[index]
      if (!pointData) return

      const primaryDatasetIndex = this.getPrimaryVisibleDatasetIndex(chart)
      if (primaryDatasetIndex === -1) {
        this.tooltipVisible = false
        this.showVerticalLine = false
        return
      }

      const meta = chart.getDatasetMeta(primaryDatasetIndex)
      const point = meta && meta.data && meta.data[index]
      if (!point) {
        this.tooltipVisible = false
        this.showVerticalLine = false
        return
      }

      const model = point._model || point
      const pointX = model.x

      const chartRect = chart.canvas.getBoundingClientRect()
      const containerRect = this.$el.getBoundingClientRect()

      const posX = Math.round(pointX + (chartRect.left - containerRect.left) + 10)
      const posY = Math.round(chartRect.top - containerRect.top + 30)

      const chartArea = chart.chartArea
      this.verticalLineTop = Math.round(chartRect.top - containerRect.top + 6)
      this.verticalLineHeight = chartArea.bottom - chartArea.top
      this.verticalX = Math.round(pointX + (chartRect.left - containerRect.left))

      const f = this.formatIndexDate(pointData.sentOn)
      this.tooltipTitle = `${f.date} ${f.time}`

      const items = []
      if (this.isLegendDatasetVisible('Kecepatan')) {
        items.push({
          color: '#8F49DE',
          label: 'Kecepatan',
          value: `${pointData.speed} Km/Jam`,
        })
      }

      if (this.hasTmsColdChainAccess && this.isLegendDatasetVisible('Suhu')) {
        let tempVal = null
        switch (this.selectedTemperature) {
          case 'Suhu 2':
            tempVal = pointData.temperature2
            break
          case 'Suhu 3':
            tempVal = pointData.temperature3
            break
          default:
            tempVal = pointData.temperature
        }

        items.push({
          color: '#FF8A00',
          label: 'Suhu',
          value: tempVal == null ? '-' : `${tempVal}°C`,
        })
      }

      if (this.hasTmsColdChainAccess && this.isLegendDatasetVisible('Pintu')) {
        const doorVal = this.selectedDoor === 'Pintu 2' ? pointData.door2 : pointData.door1

        let doorLabel = '-'

        if (doorVal === 1) {
          doorLabel = 'Buka'
        } else if (doorVal === 0) {
          doorLabel = 'Tutup'
        }

        items.push({
          color: '#4990E1',
          label: 'Pintu',
          value: doorLabel,
        })
      }

      this.tooltipItems = items
      this.$nextTick(() => {
        const adjusted = this.adjustTooltipPosition(posX, posY, this.verticalX)
        this.tooltipPos = { x: adjusted.x, y: adjusted.y }
        this.tooltipVisible = true
        this.showVerticalLine = true
      })
    },
    handleExpand() {
      this.mapCollapsed = !this.mapCollapsed
    },
  },
}
</script>

<style>
.mc-delivery-track-map-info-window {
  background: #344264;
  color: #fff;
  border-radius: 8px;
  padding: 10px;
  width: 340px;
  font-size: 13px;
  overflow-x: hidden;

  &.scrollable {
    max-height: 285px;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .mc-delivery-track-map-info-header {
    display: flex;
    justify-content: space-between;
    font-weight: 600;
    margin-bottom: 5px;

    .mc-fa {
      margin-right: -5px;
      &:before {
        color: #fff;
      }
    }
  }

  .close-btn {
    background: transparent;
    border: none;
    color: #fff;
    font-size: 20px;
    cursor: pointer;
    margin-right: -5px;
    margin-top: -5px;
  }

  .mc-delivery-track-map-info-body {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 16px;
    row-gap: 8px;
  }

  .mc-delivery-track-map-info-field {
    grid-column: 1 / -1;
  }

  .mc-delivery-track-map-info-field.is-half {
    grid-column: span 1;
  }

  .mc-delivery-track-map-info-label {
    margin: 0;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.8);
  }

  .mc-delivery-track-map-info-value {
    margin: 2px 0 0;
    line-height: 1.3;
  }

  .mc-delivery-track-map-info-divider {
    grid-column: 1 / -1;
    margin: 2px 0 4px;
  }

  hr {
    margin-top: 0px;
    margin-bottom: 5px;
    border: none;
    border-top: 1px solid #fff;
  }
}

.gm-style .gm-style-iw-c {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  max-height: none !important;
}

.gm-style .gm-style-iw-d {
  overflow: hidden !important;
  max-height: none !important;
}

.gm-style .gm-style-iw {
  overflow: visible !important;
  max-height: none !important;
}

.gm-style .gm-style-iw-t {
  padding-bottom: 20px !important;
}

/* Handle both with and without sensor analysis */
#livetrack-map.with-sensor-analysis .gm-style .gm-style-iw-d,
#livetrack-map .gm-style .gm-style-iw-d {
  overflow: hidden !important;
  max-height: none !important;
}

#livetrack-map.with-sensor-analysis .gm-style .gm-style-iw-c,
#livetrack-map .gm-style .gm-style-iw-c {
  max-height: none !important;
}

.gm-style-iw-c button.gm-ui-hover-effect {
  display: none !important;
}
</style>
